if (!customElements.get('testimonial-slider')) {
  class TestimonialSlider extends HTMLElement {
    constructor() {
      super();

      this.index = 0;
      this.timer = null;
      this.scrollFrame = null;
      this.resizeFrame = null;
      this.pauseReasons = new Set();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileViewport = window.matchMedia('(max-width: 749px)');

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onReducedMotionChange = this.onReducedMotionChange.bind(this);
      this.onAutoplayToggle = this.onAutoplayToggle.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.viewport = this.querySelector('.oh-testimonial-slider__viewport');
      this.slides = Array.from(this.querySelectorAll('[data-testimonial-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-testimonial-dot]'));
      this.currentLabel = this.querySelector('[data-testimonial-current]');
      this.previousButton = this.querySelector('[data-testimonial-previous]');
      this.nextButton = this.querySelector('[data-testimonial-next]');
      this.toggleButton = this.querySelector('[data-testimonial-toggle]');

      if (!this.viewport || this.slides.length === 0) return;

      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(Number(this.dataset.autoplayDelay) || 7000, 4000);
      this.style.setProperty('--oh-testimonial-delay', `${this.autoplayDelay}ms`);

      this.previousButton?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
      this.toggleButton?.addEventListener('click', this.onAutoplayToggle);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('touchstart', () => this.pause('touch'), { passive: true });
      this.viewport.addEventListener('touchend', () => this.resume('touch'), { passive: true });
      this.viewport.addEventListener('touchcancel', () => this.resume('touch'), { passive: true });
      this.addEventListener('mouseenter', () => this.pause('hover'));
      this.addEventListener('mouseleave', () => this.resume('hover'));
      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.resume('focus');
        }, 0);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
        if (event.key === 'ArrowRight') this.goTo(this.index + 1);
      });

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.onResize);
      this.reducedMotion.addEventListener?.('change', this.onReducedMotionChange);
      this.mobileViewport.addEventListener?.('change', this.onResize);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.syncHeight());
        this.slides.forEach((slide) => this.resizeObserver.observe(slide));
      }

      if ('IntersectionObserver' in window) {
        this.pause('offscreen');
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) this.resume('offscreen');
          else this.pause('offscreen');
        });
        this.visibilityObserver.observe(this);
      }

      this.dataset.initialized = 'true';
      this.setActive(0);
      this.syncAutoplayControl();
      this.onVisibilityChange();
      this.startAutoplay();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      window.cancelAnimationFrame(this.scrollFrame);
      window.cancelAnimationFrame(this.resizeFrame);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.onResize);
      this.reducedMotion.removeEventListener?.('change', this.onReducedMotionChange);
      this.mobileViewport.removeEventListener?.('change', this.onResize);
      this.toggleButton?.removeEventListener('click', this.onAutoplayToggle);
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pause('visibility');
      } else {
        this.resume('visibility');
      }
    }

    onReducedMotionChange() {
      if (this.reducedMotion.matches) {
        this.pause('reduced-motion');
      } else {
        this.resume('reduced-motion');
      }
      this.syncAutoplayControl();
    }

    onAutoplayToggle() {
      if (this.pauseReasons.has('user')) this.resume('user');
      else this.pause('user');
      this.syncAutoplayControl();
    }

    syncAutoplayControl() {
      if (!this.toggleButton) return;
      this.toggleButton.hidden = !this.autoplayEnabled || this.slides.length < 2 || this.reducedMotion.matches;
      this.toggleButton.textContent = this.pauseReasons.has('user')
        ? this.toggleButton.dataset.resumeLabel
        : this.toggleButton.dataset.pauseLabel;
    }

    onResize() {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => this.syncHeight());
    }

    onScroll() {
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => {
        const viewportLeft = this.viewport.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Infinity;

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - viewportLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== this.index) {
          this.setActive(closestIndex);
          this.startAutoplay();
        }
      });
    }

    goTo(index) {
      const nextIndex = (index + this.slides.length) % this.slides.length;
      const slide = this.slides[nextIndex];
      const left = this.viewport.scrollLeft + slide.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left;

      this.setActive(nextIndex);
      this.viewport.scrollTo({
        left,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.startAutoplay();
    }

    setActive(index) {
      this.index = index;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;
        slide.classList.toggle('is-active', isActive);
        if (isActive) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.currentLabel) this.currentLabel.textContent = String(index + 1).padStart(2, '0');
      this.syncHeight(index);
    }

    syncHeight(index = this.index) {
      if (!this.viewport) return;

      if (!this.mobileViewport.matches) {
        this.viewport.style.removeProperty('height');
        return;
      }

      const slide = this.slides[index];
      if (!slide) return;

      window.requestAnimationFrame(() => {
        this.viewport.style.height = `${slide.offsetHeight}px`;
      });
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      window.clearTimeout(this.timer);
      this.classList.remove('is-running');
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.startAutoplay();
    }

    startAutoplay() {
      window.clearTimeout(this.timer);
      this.classList.remove('is-running');

      if (!this.autoplayEnabled || this.slides.length < 2 || this.pauseReasons.size > 0 || this.reducedMotion.matches) {
        return;
      }

      window.requestAnimationFrame(() => {
        if (this.pauseReasons.size === 0 && !this.reducedMotion.matches) this.classList.add('is-running');
      });
      this.timer = window.setTimeout(() => this.goTo(this.index + 1), this.autoplayDelay);
    }
  }

  customElements.define('testimonial-slider', TestimonialSlider);
}
